import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, Instagram, ShoppingBag, MessageCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { SITE } from "@/data/site";
import { openWhatsApp } from "@/lib/whatsapp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.01a8.16 8.16 0 0 0 4.77 1.52V7.08a4.85 4.85 0 0 1-1.84-.39Z"/>
  </svg>
);
const WhatsAppIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 0 1 8.413 3.488 11.82 11.82 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.667 5.452l-.999 3.648 3.821-.799z"/></svg>
);

export const Contact = () => {
  const [form, setForm] = useState({ name: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Ad Soyad ve mesaj alanları zorunludur.");
      return;
    }
    setLoading(true);
    // Build WhatsApp message
    const waText =
      `Merhaba NovaLand,\n\n` +
      `Ad Soyad: ${form.name}\n\n` +
      `Mesaj: ${form.message}`;
    // Open WhatsApp IMMEDIATELY (within user gesture - avoids popup blocker)
    openWhatsApp(waText);
    // Fire-and-forget backend kayıt
    axios.post(`${API}/contact`, { name: form.name, phone: "-", email: "", message: form.message })
      .catch(() => { /* sessizce yut */ });
    toast.success("WhatsApp açılıyor...");
    setForm({ name: "", message: "" });
    setLoading(false);
  };

  const socials = [
    { name: "Instagram", icon: Instagram, href: SITE.social.instagram, handle: SITE.social.instagramHandle, testId: "social-instagram" },
    { name: "TikTok", icon: TikTokIcon, href: SITE.social.tiktok, handle: SITE.social.tiktokHandle, testId: "social-tiktok" },
    { name: "Shopier", icon: ShoppingBag, href: SITE.social.shopier, handle: "novalandaksesuar", testId: "social-shopier" },
    { name: "WhatsApp", icon: WhatsAppIcon, href: `https://wa.me/${SITE.whatsapp.number}`, handle: SITE.whatsapp.display, testId: "social-whatsapp" },
  ];

  return (
    <section id="iletisim" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <span className="eyebrow">İletişim</span>
          <h2 className="font-display text-4xl lg:text-5xl mt-3 text-[hsl(var(--nl-anthracite))]">
            Bize <em className="not-italic font-serif italic gold-text">Ulaşın</em>
          </h2>
          <div className="gold-line mt-5" />
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-6 leading-relaxed">
            Ürünler, sipariş süreci veya kombin önerileri hakkında her türlü sorunuz için sizinle
            iletişim kurmaktan mutluluk duyarız.
          </p>

          <div className="mt-8 space-y-3">
            {socials.map((s) => (
              <a
                key={s.name}
                data-testid={s.testId}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-between gap-4 px-5 py-4 border border-[hsl(var(--nl-line))] hover:border-[hsl(var(--nl-champagne))] hover:bg-[hsl(var(--nl-cream))] transition-all"
              >
                <div className="flex items-center gap-4">
                  <span className="w-10 h-10 grid place-items-center bg-[hsl(var(--nl-beige))] gold-text group-hover:bg-[hsl(var(--nl-champagne))] group-hover:text-[hsl(var(--nl-anthracite))] transition-colors">
                    <s.icon size={16} />
                  </span>
                  <div>
                    <p className="font-serif text-base text-[hsl(var(--nl-anthracite))]">{s.name}</p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">{s.handle}</p>
                  </div>
                </div>
                <span className="text-xs uppercase tracking-[0.18em] gold-text">Aç →</span>
              </a>
            ))}
          </div>

          <button
            data-testid="contact-whatsapp-cta"
            onClick={() => openWhatsApp()}
            className="btn-gold mt-8"
          >
            <MessageCircle size={16} />
            <span>Anlık WhatsApp Sohbeti</span>
          </button>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="lg:col-span-7 bg-[hsl(var(--nl-cream))] p-7 lg:p-10 border border-[hsl(var(--nl-line))]"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 gap-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="eyebrow !text-[10px]">Ad Soyad *</Label>
              <Input
                id="name"
                data-testid="contact-name"
                value={form.name}
                onChange={onChange("name")}
                placeholder="Adınız Soyadınız"
                className="bg-white rounded-none border-[hsl(var(--nl-line))] focus-visible:ring-[hsl(var(--nl-champagne))] h-12"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="message" className="eyebrow !text-[10px]">Mesajınız *</Label>
              <Textarea
                id="message"
                data-testid="contact-message"
                value={form.message}
                onChange={onChange("message")}
                rows={6}
                placeholder="Hangi ürün ilginizi çekti, nasıl yardımcı olabiliriz?"
                className="bg-white rounded-none border-[hsl(var(--nl-line))] focus-visible:ring-[hsl(var(--nl-champagne))]"
              />
            </div>
          </div>

          <button
            type="submit"
            data-testid="contact-submit"
            disabled={loading}
            className="btn-gold mt-8 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Send size={16} />
            <span>{loading ? "Yönlendiriliyor..." : "WhatsApp ile Gönder"}</span>
          </button>
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-4">
            Bilgileriniz otomatik olarak WhatsApp mesajına dönüştürülerek bize iletilir.
          </p>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;
